import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { FuseNavigation, FuseNavigationItem } from '@fuse/types';

export interface ApiMenuItem {
    code: string;
    label: string;
    route?: string;
    icon?: string;
    orderIndex: number;
    children?: ApiMenuItem[];
}

@Injectable({
    providedIn: 'root'
})
export class MenuService {
    private readonly apiUrl = environment.api_url;
    private menusSubject = new BehaviorSubject<FuseNavigation[]>([]);
    public menus$ = this.menusSubject.asObservable();

    constructor(private httpClient: HttpClient) {}

    /**
     * Récupère les menus depuis l'API pour l'utilisateur connecté
     */
    getUserMenus(mode?: string): Observable<FuseNavigation[]> {
        let url = `${this.apiUrl}/api/menu/me`;
        if (mode) {
            url += `?mode=${mode}`;
        }
        return this.httpClient.get<ApiMenuItem[]>(url)
            .pipe(
                map(apiMenus => this.convertApiMenusToFuseNavigation(apiMenus))
            );
    }

    /**
     * Rafraîchit les menus et met à jour le BehaviorSubject
     */
    refreshMenus(mode?: string): Observable<FuseNavigation[]> {
        return this.getUserMenus(mode).pipe(
            map(menus => {
                this.menusSubject.next(menus);
                return menus;
            })
        );
    }

    /**
     * Récupère les menus pour des rôles spécifiques (pour les admins)
     */
    getMenusByRoles(roles: string[]): Observable<FuseNavigation[]> {
        return this.httpClient.post<ApiMenuItem[]>(`${this.apiUrl}/api/menu/by-roles`, { roles })
            .pipe(
                map(apiMenus => this.convertApiMenusToFuseNavigation(apiMenus))
            );
    }

    /**
     * Récupère les overrides de l'utilisateur connecté
     */
    getUserOverrides(): Observable<any[]> {
        return this.httpClient.get<any[]>(`${this.apiUrl}/api/menu/overrides/me`);
    }

    /**
     * Définit un override pour un menu spécifique
     */
    setMenuOverride(menuCode: string, allowed: boolean): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}/api/menu/override/${menuCode}?allowed=${allowed}`, {});
    }

    /**
     * Assigne un menu à des rôles (pour les admins)
     */
    assignMenuToRoles(menuCode: string, roles: string[]): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}/api/menu/assign`, { code: menuCode, roles });
    }

    /**
     * Convertit les données de l'API vers le format FuseNavigation
     */
    private convertApiMenusToFuseNavigation(apiMenus: ApiMenuItem[]): FuseNavigation[] {
        if (!apiMenus || apiMenus.length === 0) {
            return [];
        }

        // Trier les menus par orderIndex
        const sortedMenus = apiMenus.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));

        // Convertir les menus
        const convertedMenus = sortedMenus.map(menu => this.convertApiMenuItemToFuseNavigationItem(menu));

        // Retourner directement les menus de premier niveau
        return convertedMenus;
    }

    /**
     * Convertit un élément de menu API vers un FuseNavigationItem
     */
    private convertApiMenuItemToFuseNavigationItem(apiMenu: ApiMenuItem): FuseNavigationItem {
        const hasChildren = apiMenu.children && apiMenu.children.length > 0;
        const hasRoute = apiMenu.route && apiMenu.route.trim() !== '' && apiMenu.route !== 'null';
        
        const fuseItem: FuseNavigationItem = {
            id: apiMenu.code,
            title: apiMenu.label || apiMenu.code,
            type: hasChildren ? 'collapsable' : 'item',
            icon: (apiMenu.icon && apiMenu.icon !== 'null') ? apiMenu.icon : (hasChildren ? 'folder' : 'fiber_manual_record')
        };

        // Pour les menus sans enfants, utiliser la route si elle existe
        if (!hasChildren && hasRoute) {
            fuseItem.url = apiMenu.route;
        }

        // Gestion des enfants (menus hiérarchiques)
        if (hasChildren) {
            fuseItem.children = apiMenu.children
                .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0))
                .map(child => this.convertApiMenuItemToFuseNavigationItem(child));
        }

        return fuseItem;
    }

    /**
     * Retourne la valeur actuelle des menus sans faire d'appel API
     */
    getCurrentMenus(): FuseNavigation[] {
        return this.menusSubject.value;
    }
} 